import { useCallback, useEffect, useState } from 'react';
import { Activity, BellRing, BookOpenText, Bot, Code2, FileBox, Fingerprint, Globe2, LogOut, PanelsTopLeft, Server, Settings2, ShieldCheck, TerminalSquare, TicketCheck, UsersRound } from 'lucide-react';
import { api, ApiClientError, jsonBody, setCsrfToken } from './api';
import { DeviceCenter } from './DeviceCenter';
import { SystemSettings } from './SystemSettings';
import { AccountNotifications } from './AccountNotifications';
import { AccountZones } from './AccountZones';
import { GlobalFiles } from './GlobalFiles';
import { GlobalScripts } from './GlobalScripts';
import { ApiDocs } from './ApiDocs';
import { LoggerPage } from './LoggerPage';
import { IdentifyLogs } from './IdentifyLogs';
import { IdentifyConsole } from './IdentifyConsole';
import { InvitationManagement, UserManagement } from './TenantAdmin';
import { AiTokens } from './AiTokens';

type LoginResult = { username: string; role: string; csrfToken: string };
type AuthState = 'loading' | 'setup' | 'guest' | 'user';
type View = 'device-center' | 'logger' | 'identify-logs' | 'identify-console' | 'zones' | 'files' | 'global-scripts' | 'users' | 'ai-tokens' | 'invitations' | 'notifications' | 'settings' | 'api-docs';

const VIEW_PATHS: Record<View, string> = {
  'device-center': '/device-center',
  logger: '/logger',
  'identify-logs': '/identify-logs',
  'identify-console': '/identify-console',
  zones: '/zones',
  files: '/files',
  'global-scripts': '/global-scripts',
  users: '/users',
  'ai-tokens': '/ai-tokens',
  invitations: '/invitations',
  notifications: '/notifications',
  settings: '/settings',
  'api-docs': '/api-docs',
};

const VIEW_LABELS: Record<View, { title: string; subtitle: string }> = {
  'device-center': { title: '设备中心', subtitle: '设备、key 与网络' },
  logger: { title: '实时日志', subtitle: '跨设备日志流' },
  'identify-logs': { title: 'Identify 日志', subtitle: '低权限写入与未关联记录' },
  'identify-console': { title: 'Identify 控制台', subtitle: '独立低权限命令通道' },
  zones: { title: '托管域名', subtitle: 'DNS Zone 与可见性' },
  files: { title: '文件管理', subtitle: '全局文件空间' },
  'global-scripts': { title: '全局脚本', subtitle: '所有设备 key 可读' },
  users: { title: '用户管理', subtitle: '额度、状态与密码重置' },
  'ai-tokens': { title: 'AI Token', subtitle: '给 AI 使用的管理凭据' },
  invitations: { title: '邀请码', subtitle: '一次性注册凭据' },
  notifications: { title: '通知通道', subtitle: 'Telegram 与邮件' },
  settings: { title: '系统设置', subtitle: '注册入口与运行参数' },
  'api-docs': { title: 'API 文档', subtitle: '设备中心接口' },
};

function viewFromPath(): View {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/logger') return 'logger';
  if (path === '/identify-logs') return 'identify-logs';
  if (path === '/identify-console') return 'identify-console';
  if (path === '/users') return 'users';
  if (path === '/ai-tokens') return 'ai-tokens';
  if (path === '/zones') return 'zones';
  if (path === '/files') return 'files';
  if (path === '/global-scripts') return 'global-scripts';
  if (path === '/invitations') return 'invitations';
  if (path === '/settings') return 'settings';
  if (path === '/notifications') return 'notifications';
  if (path === '/api-docs') return 'api-docs';
  return 'device-center';
}

function messageOf(error: unknown): string {
  if (error instanceof ApiClientError) return error.message;
  if (error instanceof Error) return error.message;
  return '操作失败';
}

export function App() {
  const [auth, setAuth] = useState<AuthState>('loading');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('user');
  const [view, setView] = useState<View>(viewFromPath);
  const [formUser, setFormUser] = useState('admin');
  const [password, setPassword] = useState('');
  const [bootstrapToken, setBootstrapToken] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const notify = useCallback((type: 'success' | 'error', text: string) => {
    setNotice({ type, text });
    window.setTimeout(() => setNotice(null), type === 'error' ? 8000 : 4500);
  }, []);

  const applySession = useCallback((session: LoginResult) => {
    setCsrfToken(session.csrfToken);
    setUsername(session.username);
    setRole(session.role);
    setView(viewFromPath());
    setAuth('user');
    setPassword('');
    setError('');
  }, []);

  useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const status = await api<{ initialized: boolean }>('/api/auth/bootstrap/status');
        if (!status.initialized) {
          if (!disposed) setAuth('setup');
          return;
        }
        const me = await api<LoginResult>('/api/auth/me');
        if (!disposed) applySession(me);
      } catch (cause) {
        if (!disposed) {
          if (cause instanceof ApiClientError && cause.status === 401) setAuth('guest');
          else { setAuth('guest'); setError(messageOf(cause)); }
        }
      }
    })();
    return () => { disposed = true; };
  }, [applySession]);

  async function login() {
    setBusy(true); setError('');
    try {
      applySession(await api<LoginResult>('/api/auth/login', { method: 'POST', body: jsonBody({ username: formUser, password }) }));
    } catch (cause) { setError(messageOf(cause)); }
    finally { setBusy(false); }
  }

  async function bootstrap() {
    setBusy(true); setError('');
    try {
      applySession(await api<LoginResult>('/api/auth/bootstrap', { method: 'POST', body: jsonBody({ token: bootstrapToken, password }) }));
    } catch (cause) { setError(messageOf(cause)); }
    finally { setBusy(false); }
  }

  async function logout() {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch { /* session may already be gone */ }
    setCsrfToken('');
    setAuth('guest');
  }

  const navigate = useCallback((next: View) => {
    window.history.pushState({}, '', VIEW_PATHS[next]);
    setView(next);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  useEffect(() => {
    const onPop = () => setView(viewFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (auth === 'user') {
      document.title = `${VIEW_LABELS[view].title} · EdgeDock`;
      if (role !== 'admin' && view !== 'device-center' && view !== 'logger' && view !== 'api-docs') setView('device-center');
    }
  }, [auth, role, view]);

  if (view === 'api-docs') return <ApiDocs />;

  if (auth === 'loading') {
    return <div className="loading-screen"><div className="loading-line" /><span>正在加载 EdgeDock…</span></div>;
  }

  if (auth === 'setup') {
    return (
      <div className="login-page">
        <section className="login-card">
          <div className="login-brand"><div className="brand-mark"><PanelsTopLeft size={22} /></div><div><strong>EdgeDock</strong><span>设备中心</span></div></div>
          <div className="login-copy"><p>首次初始化</p><h1>创建管理员</h1><span>设置控制平面的唯一管理员账号。</span></div>
          <form onSubmit={(event) => { event.preventDefault(); void bootstrap(); }}>
            <label>Bootstrap Token<input required value={bootstrapToken} onChange={(event) => setBootstrapToken(event.target.value)} autoComplete="off" /></label>
            <label>管理员密码<input required minLength={12} type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" /></label>
            {error && <div className="form-error">{error}</div>}
            <button className="primary-button wide" disabled={busy}>{busy ? '正在初始化…' : '初始化'}</button>
          </form>
          <div className="login-foot"><ShieldCheck size={16} />初始化完成后请删除一次性 Bootstrap Token</div>
        </section>
      </div>
    );
  }

  if (auth === 'guest') {
    return (
      <div className="login-page">
        <section className="login-card">
          <div className="login-brand"><div className="brand-mark"><PanelsTopLeft size={22} /></div><div><strong>EdgeDock</strong><span>设备中心</span></div></div>
          <div className="login-copy"><h1>登录 EdgeDock</h1><span>管理设备、key、安装命令与实时状态。</span></div>
          <form onSubmit={(event) => { event.preventDefault(); void login(); }}>
            <label>用户名<input required value={formUser} onChange={(event) => setFormUser(event.target.value)} autoComplete="username" /></label>
            <label>密码<input required type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" autoFocus /></label>
            {error && <div className="form-error">{error}</div>}
            <button className="primary-button wide" disabled={busy}>{busy ? '正在登录…' : '登录'}</button>
          </form>
          <div className="login-foot"><ShieldCheck size={16} />设备 key 与控制台会话严格分离</div>
        </section>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark"><PanelsTopLeft size={20} /></div>
          <div><strong>EdgeDock</strong><span>设备中心</span></div>
        </div>
        <nav className="nav-list">
          <div className="nav-item-wrap">
            <a href={VIEW_PATHS['device-center']} className={view === 'device-center' ? 'active' : ''} aria-current={view === 'device-center' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('device-center'); }}><Server size={16} />设备中心</a>
          </div>
          <p className="nav-group-label">资源</p>
          <div className="nav-item-wrap">
            <a href={VIEW_PATHS.logger} className={view === 'logger' ? 'active' : ''} aria-current={view === 'logger' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('logger'); }}><Activity size={16} />实时日志</a>
          </div>
          {role === 'admin' && <div className="nav-item-wrap">
            <a href={VIEW_PATHS['identify-logs']} className={view === 'identify-logs' ? 'active' : ''} aria-current={view === 'identify-logs' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('identify-logs'); }}><Fingerprint size={16} />Identify 日志</a>
          </div>}
          {role === 'admin' && <div className="nav-item-wrap">
            <a href={VIEW_PATHS['identify-console']} className={view === 'identify-console' ? 'active' : ''} aria-current={view === 'identify-console' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('identify-console'); }}><TerminalSquare size={16} />Identify 控制台</a>
          </div>}
          <div className="nav-item-wrap">
            <a href={VIEW_PATHS.zones} className={view === 'zones' ? 'active' : ''} aria-current={view === 'zones' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('zones'); }}><Globe2 size={16} />托管域名</a>
          </div>
          {role === 'admin' && <div className="nav-item-wrap">
            <a href={VIEW_PATHS.files} className={view === 'files' ? 'active' : ''} aria-current={view === 'files' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('files'); }}><FileBox size={16} />文件管理</a>
          </div>}
          {role === 'admin' && <div className="nav-item-wrap">
            <a href={VIEW_PATHS['global-scripts']} className={view === 'global-scripts' ? 'active' : ''} aria-current={view === 'global-scripts' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('global-scripts'); }}><Code2 size={16} />全局脚本</a>
          </div>}
          {role === 'admin' && <>
            <p className="nav-group-label">管理</p>
            <div className="nav-item-wrap">
              <a href={VIEW_PATHS.users} className={view === 'users' ? 'active' : ''} aria-current={view === 'users' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('users'); }}><UsersRound size={16} />用户管理</a>
            </div>
            <div className="nav-item-wrap">
              <a href={VIEW_PATHS['ai-tokens']} className={view === 'ai-tokens' ? 'active' : ''} aria-current={view === 'ai-tokens' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('ai-tokens'); }}><Bot size={16} />AI Token</a>
            </div>
            <div className="nav-item-wrap">
              <a href={VIEW_PATHS.invitations} className={view === 'invitations' ? 'active' : ''} aria-current={view === 'invitations' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('invitations'); }}><TicketCheck size={16} />邀请码</a>
            </div>
            <div className="nav-item-wrap">
              <a href={VIEW_PATHS.settings} className={view === 'settings' ? 'active' : ''} aria-current={view === 'settings' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('settings'); }}><Settings2 size={16} />系统设置</a>
            </div>
          </>}
          <div className="nav-item-wrap">
            <a href={VIEW_PATHS.notifications} className={view === 'notifications' ? 'active' : ''} aria-current={view === 'notifications' ? 'page' : undefined} onClick={(event) => { event.preventDefault(); navigate('notifications'); }}><BellRing size={16} />通知通道</a>
          </div>
          <div className="nav-item-wrap">
            <a href={VIEW_PATHS['api-docs']}  onClick={(event) => { event.preventDefault(); navigate('api-docs'); }}><BookOpenText size={16} />API 文档</a>
          </div>
        </nav>
        <div className="sidebar-foot"><span>{username}</span><button type="button" className="text-button" onClick={() => void logout()}><LogOut size={14} />退出</button></div>
      </aside>
      <main className="main-area">
        <header className="topbar">
          <div className="topbar-title"><div><p>{VIEW_LABELS[view].subtitle}</p><h1>{VIEW_LABELS[view].title}</h1></div></div>
          {notice && <div className={`notice ${notice.type}`}>{notice.text}</div>}
        </header>
        <div className="content">
          {view === 'device-center' && <DeviceCenter notify={notify} />}
          {view === 'logger' && <LoggerPage notify={notify} />}
          {view === 'identify-logs' && role === 'admin' && <IdentifyLogs global notify={notify} />}
          {view === 'identify-console' && role === 'admin' && <IdentifyConsole notify={notify} />}
          {view === 'zones' && <AccountZones notify={notify} onChanged={async () => {}} isAdmin={role === 'admin'} />}
          {view === 'files' && role === 'admin' && <GlobalFiles notify={notify} />}
          {view === 'global-scripts' && role === 'admin' && <GlobalScripts notify={notify} />}
          {view === 'users' && role === 'admin' && <UserManagement notify={notify} />}
          {view === 'ai-tokens' && role === 'admin' && <AiTokens notify={notify} />}
          {view === 'invitations' && role === 'admin' && <InvitationManagement notify={notify} />}
          {view === 'settings' && role === 'admin' && <SystemSettings notify={notify} />}
          {view === 'notifications' && <AccountNotifications notify={notify} />}
        </div>
      </main>
    </div>
  );
}
